import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import useLocale from '../hooks/useLocale';
import './FestschriftInfoPage.css';
import HelpButton from '../components/HelpButton';

function DownloadIcon({ size = 18 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M12 3v10m0 0 4-4m-4 4-4-4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M5 17v2a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-2"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function FestschriftInfoPage() {
  const { festschrift_id } = useParams();
  const navigate = useNavigate();
  const { getCurrentLanguage } = useLocale();
  const lang = getCurrentLanguage();

  const t = useMemo(() => (ru, en) => (lang === 'ru' ? ru : en), [lang]);

  const normalizeTitleText = (raw) => {
    let s = (raw || '').toString().trim();
    if (!s) return '';

    const wrappers = ['***', '**', '*', '___', '__', '_', '~~', '`'];
    const strip = (val, w) =>
      val.startsWith(w) && val.endsWith(w) && val.length >= w.length * 2
        ? val.slice(w.length, -w.length).trim()
        : val;

    let changed = true;
    while (changed) {
      changed = false;
      for (const w of wrappers) {
        const next = strip(s, w);
        if (next !== s) {
          s = next;
          changed = true;
        }
      }
    }
    return s;
  };

  const isNonEmpty = useMemo(() => (v) => (v || '').toString().trim().length > 0, []);

  const normalizeKey = (value) => (value || '').toString().trim().toLowerCase();

  const getTodayISOInTimeZone = (timeZone) => {
    try {
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: timeZone || 'UTC',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).formatToParts(new Date());

      const y = parts.find((p) => p.type === 'year')?.value;
      const m = parts.find((p) => p.type === 'month')?.value;
      const d = parts.find((p) => p.type === 'day')?.value;

      if (y && m && d) return `${y}-${m}-${d}`;
    } catch (e) {
      // ignore and fall back
    }
    // fallback: local date (best-effort)
    return new Date().toISOString().slice(0, 10);
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'download';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  };

  const downloadMaterial = async (m) => {
    try {
      const res = await api.get(
        `/author/publishing/${info?.festschrift?.slug || festschrift_id}/materials/${m.id}/file`,
        { responseType: 'blob' },
      );

      const cd = res.headers?.['content-disposition'] || '';
      const match = cd.match(/filename\*=UTF-8''([^;]+)|filename="([^"]+)"/i);
      const filename = decodeURIComponent(
        match?.[1] || match?.[2] || m.original_filename || 'material',
      );

      downloadBlob(new Blob([res.data]), filename);
    } catch (e) {
      console.error(e);
    }
  };

  const statusLabels = {
    draft: { ru: 'Черновик', en: 'Draft' },
    moderation: { ru: 'Модерация', en: 'Moderation' },
    published: { ru: 'Опубликован', en: 'Published' },
    archived: { ru: 'Архив', en: 'Archived' },
  };

  const reviewFormatLabels = {
    open: { ru: 'Открытое', en: 'Open' },
    double: { ru: 'Двойное слепое', en: 'Double blind' },
    single: { ru: 'Автор не видит рецензентов', en: "Author can't see reviewers" },
    single_blind_for_reviewer: {
      ru: 'Рецензенты не видят автора',
      en: "Reviewers can't see author",
    },
  };

  const principleLabels = {
    direct: { ru: 'Прямая', en: 'Direct' },
    indirect: { ru: 'Через редактора', en: 'With Editor' },
  };

  const pickI18n = (raw) => {
    if (raw && typeof raw === 'object') {
      return raw[lang] || raw.en || raw.ru || '';
    }
    return raw || '';
  };

  const getApiErrorText = (err, fallbackRu, fallbackEn) => {
    const detail = err?.response?.data?.detail;
    const translated = pickI18n(detail);
    return translated || (lang === 'ru' ? fallbackRu : fallbackEn);
  };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [info, setInfo] = useState(null);

  const [sectionId, setSectionId] = useState('');
  const [titleRu, setTitleRu] = useState('');
  const [titleEn, setTitleEn] = useState('');
  const [primaryLanguage, setPrimaryLanguage] = useState('en');
  const [abstractRu, setAbstractRu] = useState('');
  const [abstractEn, setAbstractEn] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // confirmation modal + duplicate check
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [dupChecking, setDupChecking] = useState(false);
  const [dupMatches, setDupMatches] = useState([]);
  const [dupError, setDupError] = useState('');
  const [submitError, setSubmitError] = useState('');

  // Show validation warnings only after the user has focused + blurred a field.
  const [touched, setTouched] = useState({
    sectionId: false,
    titleRu: false,
    titleEn: false,
    primaryLanguage: false,
    abstractRu: false,
    abstractEn: false,
  });

  const markTouched = (key) => {
    setTouched((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  };

  const submissionErrors = useMemo(() => {
    const errors = {};

    if (!sectionId) {
      errors.sectionId = t('Выберите секцию', 'Please select a section');
    }

    if (!primaryLanguage) {
      errors.primaryLanguage = t(
        'Выберите основной язык статьи',
        'Please select the primary language',
      );
    }

    // Names (titles): all fields must be non-empty to submit a work.
    if (!isNonEmpty(titleRu)) {
      errors.titleRu = t('Заполните заголовок (RU)', 'Title (RU) is required');
    }
    if (!isNonEmpty(titleEn)) {
      errors.titleEn = t('Заполните заголовок (EN)', 'Title (EN) is required');
    }

    // Annotations (abstracts): all fields must be non-empty to submit a work.
    if (!isNonEmpty(abstractRu)) {
      errors.abstractRu = t('Заполните аннотацию (RU)', 'Abstract (RU) is required');
    }
    if (!isNonEmpty(abstractEn)) {
      errors.abstractEn = t('Заполните аннотацию (EN)', 'Abstract (EN) is required');
    }

    return errors;
  }, [sectionId, titleRu, titleEn, primaryLanguage, abstractRu, abstractEn, isNonEmpty, t]);

  const isSubmissionValid = useMemo(
    () => Object.keys(submissionErrors).length === 0,
    [submissionErrors],
  );

  const showError = (field) => Boolean(touched[field] && submissionErrors[field]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError('');
      try {
        const { data } = await api.get(`/author/publishing/${festschrift_id}`);
        setInfo(data);

        // ACR canonicalization: if opened by numeric ID, replace URL with slug
        const loadedSlug = data?.festschrift?.slug;
        const currentIdent = String(festschrift_id || '');
        if (loadedSlug && /^\d+$/.test(currentIdent) && currentIdent !== loadedSlug) {
          const qs = window.location.search || '';
          navigate(`/publishing/${loadedSlug}/info${qs}`, { replace: true });
          return; // avoid extra state work; effect will re-run on new param
        }

        if (data.sections && data.sections.length && !sectionId) {
          const subs = data.existing_submissions?.submissions || [];
          const submitted = new Set(subs.map((x) => String(x.festschrift_section_id)));

          const firstAvailable = data.sections.find((s) => !submitted.has(String(s.id)));
          setSectionId(firstAvailable ? String(firstAvailable.id) : '');
        }
      } catch (err) {
        console.error(err);
        setError(
          lang === 'ru' ? 'Ошибка загрузки данных о сборнике' : 'Failed to load festschrift info',
        );
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [festschrift_id, lang]);

  if (loading) {
    return (
      <div className="page festschrift-page">
        <h1>{lang === 'ru' ? 'Сборник статей' : 'Festschrift'}</h1>
        <div className="loading-state">{lang === 'ru' ? 'Загрузка…' : 'Loading…'}</div>
      </div>
    );
  }

  if (error || !info) {
    return (
      <div className="page festschrift-page">
        <h1>{lang === 'ru' ? 'Сборник статей' : 'Festschrift'}</h1>
        <div className="error-state">{error || 'Not found'}</div>
      </div>
    );
  }

  const fest = info.festschrift || {};
  const sections = info.sections || [];

  const existingSubs = info.existing_submissions?.submissions || [];
  const submittedSectionIds = new Set(existingSubs.map((s) => String(s.festschrift_section_id)));

  const availableSections = sections.filter((s) => !submittedSectionIds.has(String(s.id)));

  const allSectionsSubmitted = sections.length > 0 && availableSections.length === 0;

  const festTitle = lang === 'ru' ? fest.title_ru : fest.title_en;
  const isPublished = (fest.status || '').toLowerCase() === 'published';
  const canManage = Boolean(info?.can_manage);

  const tzName = fest.timezone || 'UTC';
  const todayISO = getTodayISOInTimeZone(tzName);

  const statusKey = normalizeKey(fest.status);
  // By default show backend status (translated, same as before).
  let statusText = statusLabels[statusKey]?.[lang] || fest.status || '-';

  // Override status label only when backend status == published.
  if (statusKey === 'published') {
    const hasWindow = Boolean(fest.start_date && fest.end_date);

    // After publication date => "Festschrift published"
    if (fest.publication_date && todayISO >= fest.publication_date) {
      statusText = t('Сборник опубликован', 'Festschrift published');
    }
    // During submission window => "Submission period is open"
    else if (hasWindow && todayISO >= fest.start_date && todayISO <= fest.end_date) {
      statusText = t('Идет сбор работ', 'Submission period is open');
    }
    // After submission window and before publication date => "Review stage"
    else if (
      hasWindow &&
      todayISO > fest.end_date &&
      (!fest.publication_date || todayISO < fest.publication_date)
    ) {
      statusText = t('Этап рецензирования', 'Review stage');
    }
    // Before submission start_date => keep statusText ("Published")
  }
  // Submission window is based on start_date..end_date (date-only fields).
  // Backward-compatible behavior: if dates are missing, treat as open when status == published.
  const hasSubmissionWindow = Boolean(fest.start_date && fest.end_date);

  let stageKey = 'not_published';
  if (isPublished) {
    // Published "stage" has priority after publication_date
    if (fest.publication_date && todayISO >= fest.publication_date) {
      stageKey = 'festschrift_published';
    } else if (hasSubmissionWindow) {
      if (todayISO < fest.start_date) stageKey = 'before_submission';
      else if (todayISO > fest.end_date) stageKey = 'review_stage';
      else stageKey = 'submission_open';
    } else {
      stageKey = 'submission_open';
    }
  }

  const reviewModeKey = normalizeKey(fest.review_mode);
  const reviewModeText = reviewFormatLabels[reviewModeKey]?.[lang] || fest.review_mode || '-';

  const principleKey = normalizeKey(fest.review_principle);
  const principleText = principleLabels[principleKey]?.[lang] || fest.review_principle || '-';

  const periodText = (fest.start_date || '') + (fest.end_date ? ` – ${fest.end_date}` : '') || '-';

  const handleGoToSubmission = (articleId) => {
    if (!articleId) return;
    navigate(`/author/articles/${articleId}`);
  };

  const handleOpenConfirm = async (e) => {
    e.preventDefault();

    // normalize titles on the frontend too (so user immediately sees cleaned values)
    const normRu = normalizeTitleText(titleRu);
    const normEn = normalizeTitleText(titleEn);
    if (normRu !== titleRu) setTitleRu(normRu);
    if (normEn !== titleEn) setTitleEn(normEn);

    if (!isSubmissionValid) return;

    setSubmitError('');
    setDupError('');
    setDupMatches([]);
    setConfirmOpen(true);

    setDupChecking(true);
    try {
      const { data } = await api.post(
        `/author/publishing/${info?.festschrift?.slug || festschrift_id}/submissions/check-duplicates`,
        {
          festschrift_section_id: Number(sectionId),
          title_ru: normRu,
          title_en: normEn,
        },
      );
      setDupMatches(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setDupError(
        getApiErrorText(
          err,
          'Не удалось проверить дубликаты заголовка',
          'Failed to check duplicate titles',
        ),
      );
    } finally {
      setDupChecking(false);
    }
  };

  const handleConfirmSubmit = async () => {
    if (!isSubmissionValid) return;

    // If duplicate check found matches, do not allow submission from the UI.
    if (dupMatches.length > 0) {
      setSubmitError(
        lang === 'ru'
          ? 'Нельзя отправить заявку: в сборнике уже есть статья с таким же заголовком.'
          : 'Cannot submit: this festschrift already has an article with the same title.',
      );
      return;
    }

    const normRu = normalizeTitleText(titleRu);
    const normEn = normalizeTitleText(titleEn);

    setSubmitting(true);
    setSubmitError('');
    try {
      const { data } = await api.post(
        `/author/publishing/${info?.festschrift?.slug || festschrift_id}/submissions`,
        {
          festschrift_section_id: Number(sectionId),
          title_ru: normRu,
          title_en: normEn,
          primary_language: primaryLanguage,
          abstract_ru: abstractRu,
          abstract_en: abstractEn,
        },
      );
      const articleId = data?.article_id;
      if (articleId) {
        navigate(`/author/articles/${articleId}`);
      } else {
        setSubmitError(lang === 'ru' ? 'Не удалось создать заявку' : 'Failed to create submission');
      }
    } catch (err) {
      console.error(err);
      setSubmitError(
        getApiErrorText(err, 'Ошибка при создании заявки', 'Error creating submission'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page festschrift-page">
      <div className="page-title-row">
        <h1 className="page-title">
          {lang === 'ru' ? 'Сборник статей' : 'Festschrift'}: {festTitle}
        </h1>

        {canManage ? (
          <button
            type="button"
            className="btn secondary"
            onClick={() => navigate(`/management/publishing/${fest.slug || festschrift_id}`)}
          >
            {lang === 'ru' ? 'Перейти в управление' : 'Go to management'}
          </button>
        ) : null}
      </div>

      {canManage && !isPublished ? (
        <div className="draft-warning">
          {lang === 'ru'
            ? 'Сборник ещё не опубликован. Эту страницу видит только организатор.'
            : 'This festschrift is not published yet. Only the organizer can see this page.'}
        </div>
      ) : null}

      <div className="section-card">
        <div className="card-head">{lang === 'ru' ? 'Информация' : 'Information'}</div>

        <div className="card-body">
          <dl className="info-grid">
            <div className="info-row">
              <dt className="info-label">{lang === 'ru' ? 'Статус' : 'Status'}</dt>
              <dd className="info-value">
                <span className={`status-badge status-${statusKey || 'archived'}`}>
                  {statusText}
                </span>
              </dd>
            </div>

            <div className="info-row">
              <dt className="info-label">{lang === 'ru' ? 'Акроним' : 'Acronym'}</dt>
              <dd className="info-value">
                <div>{fest.slug ? <code>{fest.slug}</code> : '-'}</div>
              </dd>
            </div>

            <div className="info-row">
              <dt className="info-label">{lang === 'ru' ? 'Период' : 'Period'}</dt>
              <dd className="info-value">{periodText}</dd>
            </div>

            <div className="info-row">
              <dt className="info-label">{lang === 'ru' ? 'Публикация' : 'Publication'}</dt>
              <dd className="info-value">{fest.publication_date || '-'}</dd>
            </div>

            <div className="info-row">
              <dt className="info-label">
                {lang === 'ru' ? 'Формат рецензирования' : 'Review format'}
              </dt>
              <dd className="info-value">{reviewModeText}</dd>
            </div>

            <div className="info-row">
              <dt className="info-label">
                {lang === 'ru' ? 'Принцип рецензирования' : 'Review principle'}
              </dt>
              <dd className="info-value">{principleText}</dd>
            </div>

            <div className="info-row">
              <dt className="info-label">{lang === 'ru' ? 'Контактный email' : 'Contact email'}</dt>
              <dd className="info-value">
                {fest.email ? <a href={`mailto:${fest.email}`}>{fest.email}</a> : '-'}
              </dd>
            </div>
            <div className="info-row info-row-full">
              <dt className="info-label">{lang === 'ru' ? 'Описание' : 'Description'}</dt>
              <dd className="info-value">
                {(lang === 'ru' ? fest.description_ru : fest.description_en) ? (
                  <div className="description-prose">
                    {lang === 'ru' ? fest.description_ru : fest.description_en}
                  </div>
                ) : (
                  '-'
                )}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      <div className="section-card">
        <div className="card-head">
          <span>{lang === 'ru' ? 'Материалы' : 'Materials'}</span>
          <HelpButton
            helpId="festschrift.info.materials_text"
            helpLabel={lang === 'ru' ? 'Подсказка' : 'Help'}
          />
        </div>
        <div className="card-body">
          {info?.materials?.length ? (
            <ul className="materials-list">
              {info.materials.map((m) => {
                const title =
                  m?.title?.[`name_${lang}`] || m?.title?.name_en || m.original_filename;

                const comment = m?.comment?.[`name_${lang}`] || m?.comment?.name_en || '';

                return (
                  <li key={m.id} className="materials-item">
                    <div>
                      <div className="materials-item-title">{title}</div>
                      {comment ? <div className="materials-item-meta">{comment}</div> : null}
                    </div>

                    <button
                      type="button"
                      className="download-icon-btn"
                      onClick={() => downloadMaterial(m)}
                      aria-label={`${lang === 'ru' ? 'Скачать' : 'Download'}: ${m.original_filename}`}
                      title={`${lang === 'ru' ? 'Скачать' : 'Download'}: ${m.original_filename}`}
                    >
                      <DownloadIcon />
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="muted">{lang === 'ru' ? 'Материалов нет.' : 'No materials.'}</p>
          )}
        </div>
      </div>

      <div className="section-card">
        <div className="card-head">
          <span>{lang === 'ru' ? 'Участие' : 'Participation'}</span>
          <HelpButton
            helpId="festschrift.info.participation_video"
            helpLabel={lang === 'ru' ? 'Подсказка (видео)' : 'Help (video)'}
          />
        </div>
        <div className="card-body">
          {(() => {
            const alreadySubmittedBlock =
              existingSubs.length > 0 ? (
                <div className="already-submitted">
                  <p className="already-submitted-title">
                    {lang === 'ru'
                      ? 'Вы уже подали статьи в следующие разделы:'
                      : 'You have already submitted to the following sections:'}
                  </p>

                  <ul className="already-submitted-list">
                    {existingSubs.map((sub) => {
                      const sec = sections.find(
                        (s) => String(s.id) === String(sub.festschrift_section_id),
                      );
                      const secTitle = sec
                        ? lang === 'ru'
                          ? sec.title_ru
                          : sec.title_en
                        : `#${sub.festschrift_section_id}`;

                      return (
                        <li key={`${sub.festschrift_section_id}-${sub.article_id}`}>
                          <span className="already-submitted-section">{secTitle}</span>
                          <button
                            type="button"
                            className="btn primary already-submitted-btn"
                            onClick={() => handleGoToSubmission(sub.article_id)}
                          >
                            {lang === 'ru' ? 'Перейти' : 'Open'}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : null;

            if (!isPublished) {
              return (
                <p className="muted">
                  {lang === 'ru'
                    ? 'Подача заявок для этого сборника ещё не открыта.'
                    : 'Submissions for this festschrift are not open yet.'}
                </p>
              );
            }

            if (stageKey === 'before_submission') {
              return (
                <>
                  {alreadySubmittedBlock}
                  <p className="muted">
                    {lang === 'ru'
                      ? 'Период подачи ещё не начался.'
                      : 'Submission period hasn’t started yet.'}
                  </p>
                </>
              );
            }

            if (stageKey === 'review_stage') {
              return (
                <>
                  {alreadySubmittedBlock}
                  <p className="muted">
                    {lang === 'ru'
                      ? 'Подача работ закрыта. Чтобы подать заявку, свяжитесь с организатором.'
                      : 'Submission is closed. To apply, please contact the organizer.'}
                  </p>
                </>
              );
            }

            if (stageKey === 'festschrift_published') {
              return (
                <>
                  <div className="published-works-placeholder">
                    <div className="published-works-title">
                      {lang === 'ru' ? 'Публикации' : 'Works'}
                    </div>
                    <div className="published-works-text">
                      {lang === 'ru'
                        ? 'Здесь будет отображаться коллекция работ (пока заглушка).'
                        : 'A collection of works will be shown here (placeholder for now).'}
                    </div>
                  </div>
                </>
              );
            }

            // stageKey === "submission_open" (or legacy open)
            return (
              <>
                {alreadySubmittedBlock}

                {allSectionsSubmitted ? (
                  <p className="muted">
                    {lang === 'ru'
                      ? 'Вы уже подали статьи во все разделы этого сборника.'
                      : 'You have already submitted to every section of this festschrift.'}
                  </p>
                ) : (
                  <>
                    <p>
                      {lang === 'ru'
                        ? 'Заполните форму, чтобы подать заявку.'
                        : 'Fill the form to submit a paper.'}
                    </p>
                    <form onSubmit={handleOpenConfirm} className="simple-form">
                      <div className="form-row full">
                        <label>
                          {lang === 'ru' ? 'Секция' : 'Section'}
                          <span className="required-asterisk" aria-hidden="true">
                            *
                          </span>
                          :
                          <select
                            value={sectionId}
                            onChange={(e) => setSectionId(e.target.value)}
                            onBlur={() => markTouched('sectionId')}
                            className={showError('sectionId') ? 'field-invalid' : ''}
                          >
                            <option value="" disabled>
                              {lang === 'ru' ? 'Выберите секцию' : 'Select a section'}
                            </option>
                            {availableSections.map((section) => (
                              <option key={section.id} value={section.id}>
                                {lang === 'ru' ? section.title_ru : section.title_en}
                              </option>
                            ))}
                          </select>
                          {showError('sectionId') ? (
                            <div className="field-warning">{submissionErrors.sectionId}</div>
                          ) : null}
                        </label>
                      </div>

                      <div className="form-row">
                        <label>
                          {lang === 'ru' ? 'Заголовок (RU)' : 'Title (RU)'}
                          <span className="required-asterisk" aria-hidden="true">
                            *
                          </span>
                          :
                          <input
                            type="text"
                            value={titleRu}
                            onChange={(e) => setTitleRu(e.target.value)}
                            onBlur={() => {
                              markTouched('titleRu');
                              setTitleRu((v) => normalizeTitleText(v));
                            }}
                            className={showError('titleRu') ? 'field-invalid' : ''}
                          />
                          {showError('titleRu') ? (
                            <div className="field-warning">{submissionErrors.titleRu}</div>
                          ) : null}
                        </label>
                      </div>

                      <div className="form-row">
                        <label>
                          {lang === 'ru' ? 'Заголовок (EN)' : 'Title (EN)'}
                          <span className="required-asterisk" aria-hidden="true">
                            *
                          </span>
                          :
                          <input
                            type="text"
                            value={titleEn}
                            onChange={(e) => setTitleEn(e.target.value)}
                            onBlur={() => {
                              markTouched('titleEn');
                              setTitleEn((v) => normalizeTitleText(v));
                            }}
                            className={showError('titleEn') ? 'field-invalid' : ''}
                          />
                          {showError('titleEn') ? (
                            <div className="field-warning">{submissionErrors.titleEn}</div>
                          ) : null}
                        </label>
                      </div>

                      <div className="form-row full">
                        <label>
                          {lang === 'ru' ? 'Основной язык статьи' : 'Primary language'}
                          <span className="required-asterisk" aria-hidden="true">
                            *
                          </span>
                          :
                          <select
                            value={primaryLanguage}
                            onChange={(e) => setPrimaryLanguage(e.target.value)}
                            onBlur={() => markTouched('primaryLanguage')}
                            className={showError('primaryLanguage') ? 'field-invalid' : ''}
                          >
                            <option value="en">English</option>
                            <option value="ru">{lang === 'ru' ? 'Русский' : 'Russian'}</option>
                          </select>
                          {showError('primaryLanguage') ? (
                            <div className="field-warning">{submissionErrors.primaryLanguage}</div>
                          ) : null}
                        </label>
                      </div>

                      <div className="form-row full">
                        <label>
                          {lang === 'ru' ? 'Аннотация (RU)' : 'Abstract (RU)'}
                          <span className="required-asterisk" aria-hidden="true">
                            *
                          </span>
                          :
                          <textarea
                            value={abstractRu}
                            onChange={(e) => setAbstractRu(e.target.value)}
                            onBlur={() => markTouched('abstractRu')}
                            rows={4}
                            className={showError('abstractRu') ? 'field-invalid' : ''}
                          />
                          {showError('abstractRu') ? (
                            <div className="field-warning">{submissionErrors.abstractRu}</div>
                          ) : null}
                        </label>
                      </div>

                      <div className="form-row full">
                        <label>
                          {lang === 'ru' ? 'Аннотация (EN)' : 'Abstract (EN)'}
                          <span className="required-asterisk" aria-hidden="true">
                            *
                          </span>
                          :
                          <textarea
                            value={abstractEn}
                            onChange={(e) => setAbstractEn(e.target.value)}
                            onBlur={() => markTouched('abstractEn')}
                            rows={4}
                            className={showError('abstractEn') ? 'field-invalid' : ''}
                          />
                          {showError('abstractEn') ? (
                            <div className="field-warning">{submissionErrors.abstractEn}</div>
                          ) : null}
                        </label>
                      </div>

                      <div className="form-row full">
                        <button
                          type="submit"
                          className="btn primary submit-btn"
                          disabled={submitting || !isSubmissionValid}
                        >
                          {submitting
                            ? lang === 'ru'
                              ? 'Отправка...'
                              : 'Submitting...'
                            : lang === 'ru'
                              ? 'Отправить заявку'
                              : 'Submit paper'}
                        </button>
                      </div>
                    </form>
                  </>
                )}
              </>
            );
          })()}
        </div>
      </div>
      {confirmOpen ? (
        <div
          className="modal-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !submitting) setConfirmOpen(false);
          }}
        >
          <div className="modal-window" role="dialog" aria-modal="true">
            <div className="modal-head">
              <div className="modal-title">
                {lang === 'ru' ? 'Подтверждение отправки заявки' : 'Confirm submission'}
              </div>
              <button
                type="button"
                className="modal-close"
                onClick={() => !submitting && setConfirmOpen(false)}
                aria-label={lang === 'ru' ? 'Закрыть' : 'Close'}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <p className="modal-lead">
                {lang === 'ru'
                  ? 'Вы собираетесь подать заявку в выбранную секцию. Проверьте данные и подтвердите отправку.'
                  : 'You are about to submit this application to the selected section. Please review the data and confirm.'}
              </p>

              <div className="summary-grid">
                <div className="summary-item">
                  <div className="summary-label">{lang === 'ru' ? 'Секция' : 'Section'}</div>
                  <div className="summary-value">
                    {(() => {
                      const sec = sections.find((s) => String(s.id) === String(sectionId));
                      return sec ? (lang === 'ru' ? sec.title_ru : sec.title_en) : '-';
                    })()}
                  </div>
                </div>

                <div className="summary-item">
                  <div className="summary-label">
                    {lang === 'ru' ? 'Основной язык' : 'Primary language'}
                  </div>
                  <div className="summary-value">
                    {primaryLanguage === 'ru' ? (lang === 'ru' ? 'Русский' : 'Russian') : 'English'}
                  </div>
                </div>

                <div className="summary-item">
                  <div className="summary-label">
                    {lang === 'ru' ? 'Заголовок (RU)' : 'Title (RU)'}
                  </div>
                  <div className="summary-value">{normalizeTitleText(titleRu) || '-'}</div>
                </div>

                <div className="summary-item">
                  <div className="summary-label">
                    {lang === 'ru' ? 'Заголовок (EN)' : 'Title (EN)'}
                  </div>
                  <div className="summary-value">{normalizeTitleText(titleEn) || '-'}</div>
                </div>

                <div className="summary-item">
                  <div className="summary-label">
                    {lang === 'ru' ? 'Аннотация (RU)' : 'Abstract (RU)'}
                  </div>
                  <div className="summary-value prelike">{abstractRu || '-'}</div>
                </div>

                <div className="summary-item">
                  <div className="summary-label">
                    {lang === 'ru' ? 'Аннотация (EN)' : 'Abstract (EN)'}
                  </div>
                  <div className="summary-value prelike">{abstractEn || '-'}</div>
                </div>
              </div>

              {dupError ? <div className="warning-callout">{dupError}</div> : null}

              {dupChecking ? (
                <div className="muted">
                  {lang === 'ru' ? 'Проверяем дубликаты...' : 'Checking duplicates...'}
                </div>
              ) : dupMatches.length > 0 ? (
                <div className="error-callout">
                  <div className="warning-title">
                    {lang === 'ru'
                      ? 'Нельзя отправить заявку: в сборнике уже есть статья с таким же заголовком'
                      : 'Submission is blocked: this festschrift already has an article with the same title'}
                  </div>
                  <ul className="warning-list">
                    {dupMatches.map((m) => (
                      <li key={`${m.festschrift_section_id}-${m.article_id}`}>
                        <span>{lang === 'ru' ? m.section_title_ru : m.section_title_en}</span>
                        <button
                          type="button"
                          className="btn secondary warning-open"
                          onClick={() => navigate(`/author/articles/${m.article_id}`)}
                        >
                          {lang === 'ru' ? 'Открыть' : 'Open'}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {submitError ? <div className="error-callout">{submitError}</div> : null}
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn secondary"
                onClick={() => !submitting && setConfirmOpen(false)}
                disabled={submitting}
              >
                {lang === 'ru' ? 'Отмена' : 'Cancel'}
              </button>
              <button
                type="button"
                className="btn primary"
                onClick={handleConfirmSubmit}
                disabled={submitting || dupChecking || dupMatches.length > 0 || !isSubmissionValid}
              >
                {submitting
                  ? lang === 'ru'
                    ? 'Отправка...'
                    : 'Submitting...'
                  : lang === 'ru'
                    ? 'Подтвердить и отправить'
                    : 'Confirm & submit'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
